import { useEffect, useState, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { api, formatApiError, BACKEND_URL } from "@/lib/api";
import toast, { Toaster } from "react-hot-toast";
import { User, Lock, Trash2, Save, Eye, EyeOff, LogOut, BookOpen, Clock, CheckCircle, XCircle, AlertCircle } from "lucide-react";
import { useAuth } from "@/lib/auth";
import SEO from "@/components/SEO";
import {
    Dialog,
    DialogContent,
    DialogHeader,
    DialogTitle,
} from "@/components/ui/dialog";
import SecurePdfViewer from "@/components/site/SecurePdfViewer";

const TABS = [
    { id: "info",     label: "Profile Info",    icon: User },
    { id: "ebooks",   label: "আমার ই-বুক",       icon: BookOpen },
    { id: "password", label: "Change Password",  icon: Lock },
    { id: "danger",   label: "Danger Zone",      icon: Trash2 },
];

export default function Profile() {
    const navigate = useNavigate();
    const { user, loading, refresh, logout } = useAuth();
    const [tab, setTab] = useState("ebooks");

    useEffect(() => {
        if (!loading && !user) navigate("/login");
    }, [user, loading, navigate]);

    useEffect(() => {
        if (!loading && user && user.role !== "customer") navigate("/admin");
    }, [user, loading, navigate]);

    if (loading || !user) {
        return (
            <div className="min-h-[60vh] flex items-center justify-center">
                <div className="w-8 h-8 border-4 border-[#0B1E8A]/30 border-t-[#0B1E8A] rounded-full animate-spin" />
            </div>
        );
    }

    return (
        <>
            <SEO title={user.name} description="User profile" />
            <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-10">
                <Toaster
                position="top-center"
                toastOptions={{
                    style: { borderRadius: "12px", fontWeight: "600", fontSize: "14px" },
                    success: { style: { background: "#0B8A4A", color: "#fff" }, iconTheme: { primary: "#fff", secondary: "#0B8A4A" } },
                    error: { style: { background: "#D61F1F", color: "#fff" }, iconTheme: { primary: "#fff", secondary: "#D61F1F" } },
                }}
            />

            {/* Header */}
            <div className="flex items-center gap-4 mb-8">
                <div className="w-14 h-14 rounded-2xl bg-[#0B1E8A] text-white flex items-center justify-center text-2xl font-bold uppercase shadow-md">
                    {user.name?.charAt(0)}
                </div>
                <div>
                    <h1 className="text-2xl font-bold text-[#08145A]">{user.name}</h1>
                    <p className="text-sm text-slate-500">{user.email}</p>
                </div>
            </div>

            {/* Tabs */}
            <div className="flex gap-1 mb-6 bg-slate-100 p-1 rounded-2xl overflow-x-auto">
                {TABS.map(({ id, label, icon: Icon }) => (
                    <button
                        key={id}
                        onClick={() => setTab(id)}
                        className={`flex-shrink-0 flex items-center justify-center gap-2 py-2.5 px-3 rounded-xl text-sm font-semibold transition-all ${
                            tab === id ? "bg-white text-[#0B1E8A] shadow-sm" : "text-slate-500 hover:text-[#0B1E8A]"
                        }`}
                    >
                        <Icon className="w-4 h-4" />
                        <span className="hidden sm:inline">{label}</span>
                    </button>
                ))}
            </div>

            {/* Tab Content */}
            <div className="bg-white rounded-3xl shadow-sm border border-[#E2EAF4] p-6 sm:p-8">
                {tab === "info"     && <InfoTab user={user} refresh={refresh} />}
                {tab === "ebooks"   && <EbooksTab navigate={navigate} />}
                {tab === "password" && <PasswordTab />}
                {tab === "danger"   && <DangerTab logout={logout} navigate={navigate} />}
            </div>
        </div>
        </>
    );
}

// ─── Info Tab 
function InfoTab({ user, refresh }) {
    const [form, setForm] = useState({ name: user.name || "", phone: user.phone || "" });
    const [saving, setSaving] = useState(false);

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!form.name.trim()) { toast.error("Name cannot be empty."); return; }
        setSaving(true);
        try {
            await api.put("/profile", { name: form.name.trim(), phone: form.phone.trim() });
            await refresh();
            toast.success("Profile updated successfully!");
        } catch (err) {
            toast.error(formatApiError(err));
        } finally {
            setSaving(false);
        }
    };

    return (
        <form onSubmit={handleSubmit} className="space-y-5">
            <h2 className="text-lg font-bold text-[#08145A] mb-1">Personal Information</h2>

            <div>
                <label className="block text-sm font-semibold text-[#08145A] mb-1.5">Email address</label>
                <input type="email" value={user.email} disabled
                    className="w-full px-4 py-3 rounded-xl border border-[#D1DFF0] bg-slate-50 text-slate-400 text-sm font-medium cursor-not-allowed" />
                <p className="text-xs text-slate-400 mt-1">Email cannot be changed.</p>
            </div>

            <div>
                <label className="block text-sm font-semibold text-[#08145A] mb-1.5">Full Name</label>
                <input type="text" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })}
                    required placeholder="Your full name"
                    className="w-full px-4 py-3 rounded-xl border border-[#D1DFF0] bg-white/80 text-[#08145A] placeholder-slate-400 text-sm font-medium focus:outline-none focus:ring-2 focus:ring-[#0B1E8A]/30 focus:border-[#0B1E8A] transition" />
            </div>

            <div>
                <label className="block text-sm font-semibold text-[#08145A] mb-1.5">Phone Number</label>
                <input type="tel" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })}
                    placeholder="+880 1XXXXXXXXX"
                    className="w-full px-4 py-3 rounded-xl border border-[#D1DFF0] bg-white/80 text-[#08145A] placeholder-slate-400 text-sm font-medium focus:outline-none focus:ring-2 focus:ring-[#0B1E8A]/30 focus:border-[#0B1E8A] transition" />
            </div>

            <button type="submit" disabled={saving}
                className="w-full btn-primary py-3 rounded-xl font-bold text-sm tracking-wide shadow-soft disabled:opacity-60 disabled:cursor-not-allowed flex items-center justify-center gap-2">
                {saving ? (
                    <><svg className="animate-spin w-4 h-4" viewBox="0 0 24 24" fill="none">
                        <circle cx="12" cy="12" r="10" stroke="white" strokeWidth="3" strokeDasharray="60" strokeDashoffset="15" />
                    </svg>Saving…</>
                ) : (<><Save className="w-4 h-4" />Save Changes</>)}
            </button>
        </form>
    );
}

// ─── E-Books Tab 
function EbooksTab({ navigate }) {
    const [subs, setSubs] = useState([]);
    const [loading, setLoading] = useState(true);
    const [readingSub, setReadingSub] = useState(null); // { pdf_url, book_title }
    const [loadingRead, setLoadingRead] = useState(null); // book_id being loaded

    useEffect(() => {
        api.get("/profile/ebook-subscriptions")
            .then((r) => setSubs(r.data || []))
            .catch(() => toast.error("সাবস্ক্রিপশন লোড হয়নি"))
            .finally(() => setLoading(false));
    }, []);

    const handleRead = async (sub) => {
        setLoadingRead(sub.book_id);
        try {
            const r = await api.get(`/ebook/read/${sub.book_id}`);
            const rawUrl = r.data.pdf_url;
            // /api/ebook/stream/... → BACKEND_URL + /api/ebook/stream/...
            const fullUrl = rawUrl?.startsWith("/") ? `${BACKEND_URL}${rawUrl}` : rawUrl;
            setReadingSub({ pdf_url: fullUrl, book_title: sub.book_title });
        } catch (err) {
            const msg = err?.response?.data?.detail || "ই-বুক লোড করা যায়নি";
            toast.error(msg);
        } finally {
            setLoadingRead(null);
        }
    };

    const statusConfig = {
        pending:  { label: "যাচাই হচ্ছে",       icon: Clock,         color: "text-yellow-600 bg-yellow-50 border-yellow-200" },
        active:   { label: "সক্রিয়",             icon: CheckCircle,   color: "text-green-600 bg-green-50 border-green-200" },
        expired:  { label: "মেয়াদ শেষ",          icon: AlertCircle,   color: "text-slate-500 bg-slate-50 border-slate-200" },
        rejected: { label: "প্রত্যাখ্যাত",       icon: XCircle,       color: "text-red-600 bg-red-50 border-red-200" },
    };

    const formatDate = (iso) => {
        if (!iso) return "—";
        try {
            return new Date(iso).toLocaleDateString("bn-BD", { day: "numeric", month: "long", year: "numeric" });
        } catch {
            return iso.slice(0, 10);
        }
    };

    if (loading) {
        return (
            <div className="flex items-center justify-center py-12">
                <div className="w-7 h-7 border-4 border-[#0B1E8A]/20 border-t-[#0B1E8A] rounded-full animate-spin" />
            </div>
        );
    }

    return (
        <div className="space-y-5">
            <div className="flex items-center justify-between">
                <h2 className="text-lg font-bold text-[#08145A]">আমার ই-বুক সাবস্ক্রিপশন</h2>
                <button
                    onClick={() => navigate("/books/ebook")}
                    className="text-xs font-semibold text-[#0B1E8A] hover:underline"
                >
                    আরো ই-বুক দেখুন →
                </button>
            </div>

            {subs.length === 0 ? (
                <div className="text-center py-12 text-slate-400">
                    <BookOpen className="w-12 h-12 mx-auto mb-3 opacity-30" />
                    <p className="font-semibold">এখনো কোনো ই-বুক সাবস্ক্রিপশন নেই</p>
                    <p className="text-sm mt-1">ই-বুক কিনুন এবং যেকোনো জায়গা থেকে পড়ুন!</p>
                    <button
                        onClick={() => navigate("/books/ebook")}
                        className="mt-4 btn-primary px-5 py-2.5 rounded-xl text-sm font-bold"
                    >
                        ই-বুক দেখুন
                    </button>
                </div>
            ) : (
                <div className="space-y-3">
                    {subs.map((sub) => {
                        const cfg = statusConfig[sub.status] || statusConfig.pending;
                        const Icon = cfg.icon;
                        const isActive = sub.status === "active";

                        return (
                            <div key={sub.id} className="border border-[#E2EAF4] rounded-2xl p-4 flex items-center gap-4 hover:shadow-sm transition">
                                {/* Cover */}
                                <div className="w-14 h-18 flex-shrink-0">
                                    {sub.book_cover ? (
                                        <img
                                            src={sub.book_cover.startsWith("/") ? `${BACKEND_URL}${sub.book_cover}` : sub.book_cover}
                                            alt={sub.book_title}
                                            className="w-14 h-18 object-cover rounded-lg shadow-sm"
                                        />
                                    ) : (
                                        <div className="w-14 h-18 bg-slate-100 rounded-lg flex items-center justify-center">
                                            <BookOpen className="w-6 h-6 text-slate-300" />
                                        </div>
                                    )}
                                </div>

                                {/* Info */}
                                <div className="flex-1 min-w-0">
                                    <p className="font-bold text-[#08145A] text-sm truncate">{sub.book_title}</p>
                                    <p className="text-xs text-slate-500 mt-0.5">
                                        প্ল্যান: {sub.plan_days} দিন
                                        {sub.plan_price ? ` • ৳${sub.plan_price}` : ""}
                                    </p>

                                    {/* Dates */}
                                    {isActive && (
                                        <p className="text-xs text-slate-500 mt-0.5">
                                            মেয়াদ: {formatDate(sub.start_date)} → {formatDate(sub.expire_date)}
                                        </p>
                                    )}
                                    {sub.status === "pending" && (
                                        <p className="text-xs text-slate-400 mt-0.5">
                                            TxnID: <span className="font-mono">{sub.txn_id}</span>
                                        </p>
                                    )}
                                    {sub.status === "rejected" && sub.note && (
                                        <p className="text-xs text-red-500 mt-0.5">কারণ: {sub.note}</p>
                                    )}

                                    {/* Status badge */}
                                    <div className={`mt-2 inline-flex items-center gap-1 px-2 py-0.5 rounded-full border text-xs font-semibold ${cfg.color}`}>
                                        <Icon className="w-3 h-3" />
                                        {cfg.label}
                                    </div>
                                </div>

                                {/* Action */}
                                <div className="flex-shrink-0">
                                    {isActive && (
                                        <button
                                            onClick={() => handleRead(sub)}
                                            disabled={loadingRead === sub.book_id}
                                            className="btn-primary px-4 py-2 rounded-xl text-xs font-bold flex items-center gap-1.5 disabled:opacity-60"
                                        >
                                            {loadingRead === sub.book_id ? (
                                                <svg className="animate-spin w-3 h-3" viewBox="0 0 24 24" fill="none">
                                                    <circle cx="12" cy="12" r="10" stroke="white" strokeWidth="3" strokeDasharray="60" strokeDashoffset="15" />
                                                </svg>
                                            ) : (
                                                <BookOpen className="w-3 h-3" />
                                            )}
                                            পড়ুন
                                        </button>
                                    )}
                                    {sub.status === "expired" && (
                                        <button
                                            onClick={() => navigate(`/book/${sub.book_id}`)}
                                            className="px-4 py-2 rounded-xl text-xs font-bold border border-[#0B1E8A] text-[#0B1E8A] hover:bg-[#0B1E8A] hover:text-white transition"
                                        >
                                            নবায়ন করুন
                                        </button>
                                    )}
                                </div>
                            </div>
                        );
                    })}
                </div>
            )}

            {/* E-book Reader Dialog */}
            <Dialog open={!!readingSub} onOpenChange={() => setReadingSub(null)}>
                <DialogContent className="max-w-4xl">
                    <DialogHeader>
                        <DialogTitle className="text-[#08145A] text-lg">📖 {readingSub?.book_title}</DialogTitle>
                    </DialogHeader>
                    {readingSub?.pdf_url ? (
                        <SecurePdfViewer url={readingSub.pdf_url} />
                    ) : (
                        <div className="text-center py-12 text-slate-400">
                            <BookOpen className="w-12 h-12 mx-auto mb-3 opacity-30" />
                            <p className="font-semibold text-slate-500">PDF এখনো যুক্ত করা হয়নি</p>
                            <p className="text-sm mt-1">অ্যাডমিন শীঘ্রই PDF আপলোড করবেন। পরে আবার চেষ্টা করুন।</p>
                        </div>
                    )}
                </DialogContent>
            </Dialog>
        </div>
    );
}

// ─── Secure PDF Viewer (PDF.js canvas — no browser toolbar, no download) ──────

// ─── Password Tab ─────────────────────────────────────────────────────
function PasswordTab() {
    const [form, setForm] = useState({ current_password: "", new_password: "", confirm: "" });
    const [show, setShow] = useState({ current: false, new: false, confirm: false });
    const [saving, setSaving] = useState(false);

    const toggle = (field) => setShow((s) => ({ ...s, [field]: !s[field] }));

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (form.new_password.length < 8) { toast.error("New password must be at least 8 characters."); return; }
        if (form.new_password !== form.confirm) { toast.error("Passwords do not match."); return; }
        setSaving(true);
        try {
            await api.put("/profile/change-password", { current_password: form.current_password, new_password: form.new_password });
            toast.success("Password changed successfully!");
            setForm({ current_password: "", new_password: "", confirm: "" });
        } catch (err) {
            toast.error(formatApiError(err));
        } finally {
            setSaving(false);
        }
    };

    const fields = [
        { key: "current_password", label: "Current Password", showKey: "current" },
        { key: "new_password",     label: "New Password",     showKey: "new" },
        { key: "confirm",          label: "Confirm New Password", showKey: "confirm" },
    ];

    return (
        <form onSubmit={handleSubmit} className="space-y-5">
            <h2 className="text-lg font-bold text-[#08145A] mb-1">Change Password</h2>
            {fields.map(({ key, label, showKey }) => (
                <div key={key}>
                    <label className="block text-sm font-semibold text-[#08145A] mb-1.5">{label}</label>
                    <div className="relative">
                        <input type={show[showKey] ? "text" : "password"} value={form[key]}
                            onChange={(e) => setForm({ ...form, [key]: e.target.value })} required placeholder="••••••••"
                            className="w-full pl-4 pr-11 py-3 rounded-xl border border-[#D1DFF0] bg-white/80 text-[#08145A] placeholder-slate-400 text-sm font-medium focus:outline-none focus:ring-2 focus:ring-[#0B1E8A]/30 focus:border-[#0B1E8A] transition" />
                        <button type="button" onClick={() => toggle(showKey)}
                            className="absolute inset-y-0 right-3.5 flex items-center text-slate-400 hover:text-[#0B1E8A] transition-colors">
                            {show[showKey] ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                        </button>
                    </div>
                </div>
            ))}
            <button type="submit" disabled={saving}
                className="w-full btn-primary py-3 rounded-xl font-bold text-sm tracking-wide shadow-soft disabled:opacity-60 disabled:cursor-not-allowed flex items-center justify-center gap-2">
                {saving ? (
                    <><svg className="animate-spin w-4 h-4" viewBox="0 0 24 24" fill="none">
                        <circle cx="12" cy="12" r="10" stroke="white" strokeWidth="3" strokeDasharray="60" strokeDashoffset="15" />
                    </svg>Updating…</>
                ) : (<><Lock className="w-4 h-4" />Update Password</>)}
            </button>
        </form>
    );
}

// ─── Danger Tab ───────────────────────────────────────────────────────
function DangerTab({ logout, navigate }) {
    const [confirmText, setConfirmText] = useState("");
    const [deleting, setDeleting] = useState(false);

    const handleLogout = async () => { await logout(); };

    const handleDelete = async () => {
        if (confirmText !== "DELETE") { toast.error('Type "DELETE" to confirm.'); return; }
        setDeleting(true);
        try {
            await api.delete("/profile");
            await logout();
            toast.success("Account deleted.");
        } catch (err) {
            toast.error(formatApiError(err));
        } finally {
            setDeleting(false);
        }
    };

    return (
        <div className="space-y-6">
            <h2 className="text-lg font-bold text-[#08145A] mb-1">Danger Zone</h2>

            <div className="border border-[#E2EAF4] rounded-2xl p-5">
                <h3 className="font-semibold text-[#08145A] mb-1">Log out</h3>
                <p className="text-sm text-slate-500 mb-4">You will be signed out of your account on this device.</p>
                <button onClick={handleLogout}
                    className="flex items-center gap-2 px-5 py-2.5 rounded-xl border border-[#0B1E8A] text-[#0B1E8A] text-sm font-bold hover:bg-[#0B1E8A] hover:text-white transition-all">
                    <LogOut className="w-4 h-4" />Logout
                </button>
            </div>

            <div className="border border-[#D61F1F]/30 rounded-2xl p-5 bg-red-50/40">
                <h3 className="font-semibold text-[#D61F1F] mb-1">Delete Account</h3>
                <p className="text-sm text-slate-500 mb-4">This is permanent and cannot be undone. All your data will be removed.</p>
                <input type="text" value={confirmText} onChange={(e) => setConfirmText(e.target.value)}
                    placeholder='Type "DELETE" to confirm'
                    className="w-full px-4 py-2.5 rounded-xl border border-[#D1DFF0] text-sm font-medium text-[#08145A] focus:outline-none focus:ring-2 focus:ring-[#D61F1F]/30 focus:border-[#D61F1F] mb-3 transition" />
                <button onClick={handleDelete} disabled={deleting || confirmText !== "DELETE"}
                    className="flex items-center gap-2 px-5 py-2.5 rounded-xl bg-[#D61F1F] text-white text-sm font-bold hover:bg-red-700 disabled:opacity-40 disabled:cursor-not-allowed transition-all">
                    {deleting ? (
                        <><svg className="animate-spin w-4 h-4" viewBox="0 0 24 24" fill="none">
                            <circle cx="12" cy="12" r="10" stroke="white" strokeWidth="3" strokeDasharray="60" strokeDashoffset="15" />
                        </svg>Deleting…</>
                    ) : (<><Trash2 className="w-4 h-4" />Delete My Account</>)}
                </button>
            </div>
        </div>
    );
}